import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';

interface PnLReportProps {
  entityId: string;
  periodId: string;
}

interface EntityData {
  entityId: string;
  entityName: string;
  operatingProfit: Record<string, number>;
  operatingExpenses: Record<string, number>;
  netProfit: Record<string, number>;
  dividends: Record<string, number>;
}

interface PnLData {
  dates: string[];
  totals: {
    operatingProfit: Record<string, number>;
    operatingExpenses: Record<string, number>;
    netProfit: Record<string, number>;
    dividends: Record<string, number>;
  };
  byEntity: EntityData[];
  period: { start: string; end: string };
}

export default function PnLReport({ entityId, periodId }: PnLReportProps) {
  const { t } = useTranslation('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseInt(searchParams.get('days') || '14', 10) || 14;

  const setDays = useCallback((d: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (d === 14) next.delete('days'); else next.set('days', String(d));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data, isLoading } = useQuery<PnLData>({
    queryKey: ['report-pnl', entityId, periodId, days],
    queryFn: async () => {
      const res = await fetch(`/api/reports/pnl?days=${days}${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
  });

  if (isLoading) return <LoadingState message={t('pnl.loading')} />;

  if (!data?.dates?.length) {
    return (
      <div className="card">
        <EmptyState title={t('pnl.empty.title')} description={t('pnl.empty.description')} />
      </div>
    );
  }

  // Format date for column header (e.g., "15 Nov" or "15 ноя")
  const formatDateHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    // Use short month format based on locale
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${day} ${month}`;
  };

  const rowLabels = {
    operatingProfit: t('pnl.operatingProfit'),
    operatingExpenses: t('pnl.operatingExpenses'),
    netProfit: t('pnl.netProfit'),
    dividends: t('pnl.dividends'),
  };

  const renderDataRow = (
    label: string,
    data: Record<string, number>,
    dates: string[],
    isExpense = false,
    isProfit = false
  ) => (
    <tr>
      <td className="row-label">{label}</td>
      {dates.map((date) => {
        const value = data[date] || 0;
        const className = isExpense && value > 0
          ? 'text-error'
          : isProfit && value > 0
            ? 'text-success'
            : isProfit && value < 0
              ? 'text-error'
              : '';
        return (
          <td key={date} className={`cell-value ${className}`}>
            {value !== 0 ? formatAmount(Math.abs(value).toString()) : '-'}
          </td>
        );
      })}
    </tr>
  );

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="controls-row">
          <span className="control-label">{t('pnl.period')}:</span>
          <select
            className="form-input"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value="7">7 {t('balances.days')}</option>
            <option value="14">14 {t('balances.days')}</option>
            <option value="30">30 {t('balances.days')}</option>
            <option value="90">90 {t('balances.days')}</option>
          </select>
        </div>
      </div>

      <div className="card">
        <div className="pnl-table-wrapper">
          <table className="pnl-table">
            <thead>
              <tr>
                <th className="header-label">{t('pnl.tableTitle')}</th>
                {data.dates.map((date) => (
                  <th key={date} className="header-date">{formatDateHeader(date)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Totals Section */}
              <tr className="section-header">
                <td colSpan={data.dates.length + 1}>{t('pnl.totalSection')}</td>
              </tr>
              {renderDataRow(rowLabels.operatingProfit, data.totals.operatingProfit, data.dates, false, true)}
              {renderDataRow(rowLabels.operatingExpenses, data.totals.operatingExpenses, data.dates, true)}
              {renderDataRow(rowLabels.netProfit, data.totals.netProfit, data.dates, false, true)}
              {renderDataRow(rowLabels.dividends, data.totals.dividends, data.dates, true)}

              {/* Entity Sections */}
              {data.byEntity.map((entity) => (
                <>
                  <tr key={`header-${entity.entityId}`} className="section-header entity-header">
                    <td colSpan={data.dates.length + 1}>{entity.entityName}</td>
                  </tr>
                  {renderDataRow(rowLabels.operatingProfit, entity.operatingProfit, data.dates, false, true)}
                  {renderDataRow(rowLabels.operatingExpenses, entity.operatingExpenses, data.dates, true)}
                  {renderDataRow(rowLabels.netProfit, entity.netProfit, data.dates, false, true)}
                  {renderDataRow(rowLabels.dividends, entity.dividends, data.dates, true)}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        .controls-row {
          display: flex;
          gap: 12px;
          align-items: center;
        }
        .control-label {
          font-size: 14px;
          color: var(--color-text-muted);
        }

        .pnl-table-wrapper {
          overflow-x: auto;
          overflow-y: visible;
        }

        .pnl-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 12px;
          white-space: nowrap;
        }

        .pnl-table th,
        .pnl-table td {
          border: 1px solid var(--color-border);
          padding: 6px 10px;
        }

        .pnl-table thead th {
          background: var(--color-table-header);
          font-weight: 600;
          text-align: center;
        }

        .header-label {
          text-align: left !important;
          min-width: 280px;
          background: #e8f5e9 !important;
        }

        .header-date {
          min-width: 70px;
          background: #e8f5e9 !important;
        }

        .section-header {
          background: #b2dfdb !important;
          font-weight: 600;
        }

        .section-header td {
          padding: 8px 10px;
        }

        .entity-header {
          background: #80cbc4 !important;
        }

        .row-label {
          text-align: left;
          padding-left: 16px !important;
          color: var(--color-text);
        }

        .cell-value {
          text-align: right;
          font-family: monospace;
        }

        .text-success {
          color: var(--color-success);
        }

        .text-error {
          color: var(--color-error);
        }

        /* Alternating row backgrounds for readability */
        .pnl-table tbody tr:nth-child(4n+2),
        .pnl-table tbody tr:nth-child(4n+3) {
          background: var(--color-table-row-alt);
        }

        /* Dark theme adjustments */
        :root[data-theme="dark"] .header-label,
        :root[data-theme="dark"] .header-date {
          background: #1b4332 !important;
        }

        :root[data-theme="dark"] .section-header {
          background: #2d6a4f !important;
        }

        :root[data-theme="dark"] .entity-header {
          background: #40916c !important;
        }
      `}</style>
    </div>
  );
}
